import ReactGA from 'react-ga4';
import LogRocket from 'logrocket';
import useApi from './useApi';
import mixpanel from 'mixpanel-browser';
import { ELocalStorageKeys } from '../constants/enums';
import { webStorageUtil } from 'state/webStorage.state.service';
import { useState } from 'react';
import { isIOS13 } from 'react-device-detect';
import { clarity } from 'react-microsoft-clarity';

declare global {
  interface Window {
    logRocketDisable?: boolean;
    clarity?: {
      consent?: (enabled: boolean) => void;
    };
    gtag?: (
      command: 'config' | 'set' | 'event' | 'consent' | 'get',
      targetId: string,
      fieldNameOrParams?: string | Record<string, any>,
      callback?: (value: string) => void
    ) => void;
    [key: `ga-disable-${string}`]: boolean;
  }
}

export const CLARITY_ID = 'ji7njsuutu';

export default function useAnalytics() {
  const API = useApi({});
  const [mixInitialized, setMixInitialized] = useState(false);
  const [gaInitialized, setGaInitialized] = useState(false);
  const mixPanelId = '36bc8102bd15fc0a7471651e632a1c1d';
  const publisherMetaData = API.getPublisherMeta.data;

  const hasAnalyticsConsent = webStorageUtil.get(ELocalStorageKeys.ANALYTICS);

  const initializeGA = (publisherMetaData: any) => {
    if (!publisherMetaData.externalServicesConfig.gaActive || gaInitialized) return;

    try {
      ReactGA.initialize(publisherMetaData.integration.googleAnalyticsMeasurementId, {
        testMode: !process.env.NODE_ENV || process.env.NODE_ENV === 'development',
      });
      setGaInitialized(true);

      ReactGA.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted',
      });

      // Send first pageview to ensure client_id is generated
      ReactGA.send({ hitType: 'pageview', page: window.location.pathname });

      // Try to get client_id after a short delay to ensure GA is ready
      setTimeout(() => {
        if (window.gtag) {
          window.gtag(
            'get',
            publisherMetaData.integration.googleAnalyticsMeasurementId,
            'client_id',
            (clientId: string) => {
              if (clientId) {
                localStorage.setItem('ga_client_id', clientId);
              }
            }
          );
        }
      }, 1000);

    } catch (error) {
      console.warn('Error initializing GA:', error);
    }
  };

  const initializeMixpanel = (publisherMetaData: any) => {
    if (!publisherMetaData?.externalServicesConfig.mixpanelActive || mixInitialized) return;

    try {
      mixpanel.init(mixPanelId, { api_host: 'https://api-eu.mixpanel.com', debug: false });
      setMixInitialized(true);
    } catch (error) {
      console.warn('Error initializing Mixpanel:', error);
    }
  };

  const initializeClarity = () => {
    if (window.clarity) return;

    try {
      clarity.init(CLARITY_ID);
    } catch (error) {
      console.warn('Error initializing Clarity:', error);
    }
  };

  const initializeLogRocket = (publisherMetaData: any) => {
    if (
      !publisherMetaData?.externalServicesConfig.logRocketActive ||
      window.logRocketDisable ||
      !isIOS13
    ) return;

    try {
      LogRocket.init(`${publisherMetaData.integration.logRocketAppId}/main`);
      LogRocket.identify(mixpanel.get_distinct_id());
    } catch (error) {
      console.warn('Error initializing LogRocket:', error);
    }
  };


  const optIn = () => {
    try {
      webStorageUtil.set(ELocalStorageKeys.ANALYTICS, JSON.stringify(true));

      if (publisherMetaData?.integration.googleAnalyticsMeasurementId) {
        const gaDisableKey = `ga-disable-${publisherMetaData.integration.googleAnalyticsMeasurementId}` as `ga-disable-${string}`;
        window[gaDisableKey] = false;
      }

      initializeGA(publisherMetaData);
      initializeMixpanel(publisherMetaData);
      initializeClarity();
      window.logRocketDisable = false;
      initializeLogRocket(publisherMetaData);

    } catch (error) {
      console.warn('Error during analytics opt-in:', error);
    }
  };


  const init = () => {
    initializeMixpanel(publisherMetaData);
    if (hasAnalyticsConsent !== 'true') {
      window.logRocketDisable = true;
      return;
    }

    initializeGA(publisherMetaData);
    initializeClarity();
    initializeLogRocket(publisherMetaData);
  };


  const optOut = () => {
    try {
      webStorageUtil.set(ELocalStorageKeys.ANALYTICS, JSON.stringify(false));

      if (typeof ReactGA !== 'undefined' && ReactGA.gtag) {
        ReactGA.gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
        });
      }

      if (publisherMetaData?.integration.googleAnalyticsMeasurementId) {
        const gaDisableKey = `ga-disable-${publisherMetaData.integration.googleAnalyticsMeasurementId}` as `ga-disable-${string}`;
        window[gaDisableKey] = true;
      }

    } catch (error) {
      console.warn('Error during analytics opt-out:', error);
    }
  };

  return {
    init,
    optOut,
    optIn,
  };
}
